import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { Loader2, CalendarDays, ExternalLink, Search, Award } from "lucide-react";

const REGIONS = ["Both", "India", "Global"];
const FIELDS = ["All", "Engineering", "Medical", "Law", "Design & Arts", "Commerce", "Business", "Architecture", "Science", "Any"];

export default function CalendarPage() {
  const [exams, setExams] = useState([]);
  const [schols, setSchols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [region, setRegion] = useState("Both");
  const [field, setField] = useState("All");
  const [tab, setTab] = useState("exams");

  const load = async () => {
    const [e, s] = await Promise.all([
      api.get("/calendar/exams", { params: { region: region === "Both" ? undefined : region, field: field === "All" ? undefined : field }}),
      api.get("/calendar/scholarships", { params: { region: region === "Both" ? undefined : region }}),
    ]);
    setExams(e.data); setSchols(s.data);
  };

  useEffect(() => { load().finally(() => setLoading(false)); /* eslint-disable-next-line */ }, [region, field]);

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-6 py-10" data-testid="calendar-page">
        <p className="text-[11px] uppercase tracking-[0.2em] text-yellow-300">Calendar</p>
        <h1 className="font-display text-4xl md:text-5xl text-white mt-2">Exams & Scholarships</h1>
        <p className="text-slate-400 mt-2 max-w-xl">Track key entrance exams and top scholarships. Set reminders and never miss a deadline.</p>

        {/* Tabs */}
        <div className="mt-6 inline-flex bg-white/5 border border-white/10 rounded-full p-1">
          <button data-testid="cal-tab-exams" onClick={() => setTab("exams")} className={`px-4 py-1.5 rounded-full text-sm font-bold ${tab === "exams" ? "text-slate-900" : "text-slate-300"}`} style={tab === "exams" ? { background: "#FBBF24" } : {}}>
            <CalendarDays className="inline w-4 h-4 mr-1" /> Entrance Exams
          </button>
          <button data-testid="cal-tab-schols" onClick={() => setTab("schols")} className={`px-4 py-1.5 rounded-full text-sm font-bold ${tab === "schols" ? "text-slate-900" : "text-slate-300"}`} style={tab === "schols" ? { background: "#FBBF24" } : {}}>
            <Award className="inline w-4 h-4 mr-1" /> Scholarships
          </button>
        </div>

        {/* Filters */}
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400 uppercase tracking-[0.15em] font-bold">Region</span>
            {REGIONS.map((r) => (
              <button key={r} data-testid={`cal-region-${r}`} onClick={() => setRegion(r)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border transition ${region === r ? "text-white" : "border-white/10 text-slate-300 hover:bg-white/5"}`}
                style={region === r ? { background: "#3B82F6", borderColor: "#3B82F6" } : {}}>{r}</button>
            ))}
          </div>
          {tab === "exams" && (
            <div className="flex flex-wrap gap-2">
              {FIELDS.map((f) => (
                <button key={f} data-testid={`cal-field-${f}`} onClick={() => setField(f)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold border transition ${field === f ? "text-slate-900" : "border-white/10 text-slate-300 hover:bg-white/5"}`}
                  style={field === f ? { background: "#FBBF24", borderColor: "#FBBF24" } : {}}>{f}</button>
              ))}
            </div>
          )}
        </div>

        {loading ? (
          <div className="mt-8 text-slate-300 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : tab === "exams" ? (
          <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {exams.map((e) => (
              <div key={e.id} data-testid={`exam-${e.id}`} className="brimi-card p-5">
                <div className="flex items-start justify-between">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "#DBEAFE", color: "#1D4ED8" }}>
                    <CalendarDays className="w-5 h-5" />
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ background: e.region === "India" ? "#DBEAFE" : "#FEE2E2", color: e.region === "India" ? "#1D4ED8" : "#B91C1C" }}>{e.region}</span>
                </div>
                <p className="font-display text-lg text-slate-900 mt-3">{e.name}</p>
                <p className="text-xs text-slate-500 mt-1">{e.eligibility}</p>
                <div className="mt-3 p-3 rounded-lg bg-yellow-50 border border-yellow-100">
                  <p className="text-[10px] font-bold text-yellow-800 uppercase tracking-[0.15em]">Typical dates</p>
                  <p className="text-sm text-slate-800 font-semibold">{e.typical_dates}</p>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {(e.fields || []).slice(0,2).map((f) => <span key={f} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-bold">{f}</span>)}
                </div>
                {e.url && (
                  <a href={e.url} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1 text-xs font-bold text-blue-600 hover:underline">
                    Official site <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {schols.map((s) => (
              <div key={s.id} data-testid={`schol-${s.id}`} className="brimi-card p-5">
                <div className="flex items-start justify-between">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "#FEF3C7", color: "#92400E" }}>
                    <Award className="w-5 h-5" />
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ background: s.region === "India" ? "#DBEAFE" : "#FEE2E2", color: s.region === "India" ? "#1D4ED8" : "#B91C1C" }}>{s.region}</span>
                </div>
                <p className="font-display text-lg text-slate-900 mt-3">{s.name}</p>
                <p className="text-xs text-slate-500 mt-1">{s.who}</p>
                <div className="mt-3 p-3 rounded-lg bg-emerald-50 border border-emerald-100">
                  <p className="text-[10px] font-bold text-emerald-800 uppercase tracking-[0.15em]">Amount</p>
                  <p className="text-sm text-slate-800 font-semibold">{s.amount}</p>
                </div>
                {s.url && (
                  <a href={s.url} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1 text-xs font-bold text-blue-600 hover:underline">
                    Apply / Info <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
